import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Empty, Skeleton, Spin, Tooltip } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import { ContactsLetterGroup, countContactsThroughLetter, limitContactGroups } from '../types/ContactDisplay';
import { ContactFollowedBy, ContactHeatPill, ContactTagList } from './ContactTags';
import ContactPreview from './ContactPreview';
import ContactsAlphabetIndex from './ContactsAlphabetIndex';

export const getContactsLetterAnchor = (letter: string) => `contacts-letter-${letter === '#' ? 'other' : letter}`;

interface ContactsListProps {
  groups: ContactsLetterGroup[];
  selectedContactId: string | null;
  isLoading: boolean;
  isLoadingMore: boolean;
  showAlphabetIndex: boolean;
  onSelect: (contactId: string) => void;
}

// Height of the sticky column header, same as --contacts-head-height in ContactsPage.css
const CONTACTS_HEAD_HEIGHT = 40;
// Rows rendered per step: every contact is loaded, but thousands of rows at once make the page sluggish
const CONTACTS_RENDER_STEP = 100;
// Distance to the bottom of the list from which the next rows are rendered
const CONTACTS_RENDER_THRESHOLD = 600;

const ContactsList: React.FC<ContactsListProps> = ({
  groups,
  selectedContactId,
  isLoading,
  isLoadingMore,
  showAlphabetIndex,
  onSelect,
}) => {
  const isEmpty = groups.every((group) => group.contacts.length === 0);
  const listRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(CONTACTS_RENDER_STEP);
  const [activeLetter, setActiveLetter] = useState<string | null>(null);
  // Letter picked in the index, scrolled to once its rows are rendered
  const pendingLetterRef = useRef<string | null>(null);

  const contactsCount = useMemo(() => groups.reduce((total, group) => total + group.contacts.length, 0), [groups]);
  const visibleGroups = useMemo(() => limitContactGroups(groups, visibleCount), [groups, visibleCount]);
  const availableLetters = useMemo(() => new Set(groups.map((group) => group.letter)), [groups]);
  const hasHiddenRows = visibleCount < contactsCount;

  // The current letter is the last group whose top went under the sticky column header
  const updateActiveLetter = useCallback(() => {
    const list = listRef.current;
    if (!list) return;
    const groupElements = Array.from(list.querySelectorAll<HTMLElement>('[data-letter]'));
    let letter = groupElements[0]?.dataset.letter ?? null;
    for (const element of groupElements) {
      if (element.offsetTop > list.scrollTop + CONTACTS_HEAD_HEIGHT) break;
      letter = element.dataset.letter ?? null;
    }
    setActiveLetter(letter);
  }, []);

  const handleScroll = () => {
    updateActiveLetter();
    const list = listRef.current;
    if (!list || !hasHiddenRows) return;
    if (list.scrollHeight - list.scrollTop - list.clientHeight < CONTACTS_RENDER_THRESHOLD) {
      setVisibleCount((count) => count + CONTACTS_RENDER_STEP);
    }
  };

  const scrollToLetter = (letter: string) =>
    document.getElementById(getContactsLetterAnchor(letter))?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const selectLetter = (letter: string) => {
    const neededCount = countContactsThroughLetter(groups, letter);
    if (neededCount <= visibleCount) {
      scrollToLetter(letter);
      return;
    }
    pendingLetterRef.current = letter;
    setVisibleCount(neededCount);
  };

  // Filters and new pages move the groups without any scroll
  useEffect(() => {
    updateActiveLetter();
  }, [visibleGroups, isLoading, updateActiveLetter]);

  useEffect(() => {
    if (!pendingLetterRef.current) return;
    scrollToLetter(pendingLetterRef.current);
    pendingLetterRef.current = null;
  }, [visibleGroups]);

  return (
    <>
      <div className="contacts__list" ref={listRef} onScroll={handleScroll}>
        <div className="contacts__row contacts__row--head">
          <span>Contact</span>
          <span>Numéro principal</span>
          <span>Typologie</span>
          <span>Chaleur</span>
          <span>Tags personnalisés</span>
          <span>Suivi par</span>
          <span>Dernière interaction</span>
        </div>

        {isLoading ? (
          <div className="contacts__list-loading">
            <Skeleton active paragraph={{ rows: 8 }} />
          </div>
        ) : isEmpty ? (
          <Empty className="contacts__empty" description="Aucun contact" />
        ) : (
          visibleGroups.map((group) => (
            <div
              key={group.letter || 'all'}
              id={group.letter ? getContactsLetterAnchor(group.letter) : undefined}
              data-letter={group.letter || undefined}
              className="contacts__group"
            >
              {group.letter && (
                <div className={classNames('contacts__letter', { 'contacts__letter--label': group.isLabel })}>
                  {group.heatLevel !== undefined && (
                    <span className={classNames('contacts__heat-dot', group.heatLevel && `contacts__heat--${group.heatLevel}`)} />
                  )}
                  {group.letter}
                </div>
              )}
              {group.contacts.map((contact) => (
                <button
                  key={contact.id}
                  type="button"
                  className={classNames('contacts__row', { 'contacts__row--active': contact.id === selectedContactId })}
                  onClick={() => onSelect(contact.id)}
                >
                  <ContactPreview contact={contact}>
                    <span className="contacts__contact">
                      <span className="contacts__avatar">{contact.initials}</span>
                      <span className="contacts__name">{contact.fullName}</span>
                      {contact.isConfidential && (
                        <Tooltip title="Confidentiel - non enregistré">
                          <LockOutlined className="contacts__name-confidential" aria-label="Confidentiel" />
                        </Tooltip>
                      )}
                    </span>
                  </ContactPreview>
                  <span className="contacts__phone">{contact.phone}</span>
                  <span><ContactTagList tags={contact.typologies} max={1} title="Typologies" /></span>
                  <span><ContactHeatPill heat={contact.heat} level={contact.heatLevel} /></span>
                  <span><ContactTagList tags={contact.customTags} max={1} title="Tags personnalisés" /></span>
                  <span><ContactFollowedBy label={contact.followedBy} isOwn={contact.isOwn} /></span>
                  <span className="contacts__date">{contact.lastInteractionLabel}</span>
                </button>
              ))}
            </div>
          ))
        )}

        {isLoadingMore && !hasHiddenRows && (
          <div className="contacts__list-more">
            <Spin size="small" /> Chargement des contacts suivants…
          </div>
        )}
      </div>

      {showAlphabetIndex && !isLoading && (
        <ContactsAlphabetIndex availableLetters={availableLetters} activeLetter={activeLetter} onSelect={selectLetter} />
      )}
    </>
  );
};

export default ContactsList;
